(function () {
  "use strict";

  class ProductImage extends HTMLElement {
    constructor() {
      super();
      this.isNonTouchDevice = false;
    }

    static get observedAttributes() {
      return ["product"];
    }

    get product() {
      return this._product;
    }

    set product(value) {
      this._product = value;
      this.render();
    }

    connectedCallback() {
      // Check if device supports hover (non-touch device)
      const supportsHover = window.matchMedia('(hover: hover)').matches;
      this.isNonTouchDevice = supportsHover;
      if (this._product) {
        this.render();
      }
    }

    imgURL(src, size) {
      if (!src) {
        return '';
      }
      // remove any current image size then add the new image size
      return src
        .replace(
          /_(pico|icon|thumb|small|compact|medium|large|grande|original|1024x1024|2048x2048|master)+\./g,
          '.',
        )
        .replace(/\.jpg|\.png|\.webp|\.gif|\.jpeg/g, function (match) {
          return '_' + size + match;
        });
    }

    async getProductMediaData() {
      if (!this._product?.productId) {
        return { video: null, images: [] };
      }

      const token = document.querySelector(".rcc-search")?.dataset.token;
      if (!token) {
        console.warn("Storefront API token not found");
        return { video: null, images: [] };
      }

      const productGid = `gid://shopify/Product/${this._product.productId}`;

      const query = `
        query getProductMedia($id: ID!) {
          node(id: $id) {
            ... on Product {
              media(first: 5) {
                nodes {
                  mediaContentType
                  alt
                  ... on Video {
                    sources {
                      url
                      mimeType
                      format
                      height
                      width
                    }
                  }
                  ... on MediaImage {
                    image {
                      url
                      height
                      width
                    }
                  }
                }
              }
            }
          }
        }
      `;

      try {
        const response = await fetch("/api/2025-01/graphql.json", {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
            "X-Shopify-Storefront-Access-Token": token,
          },
          body: JSON.stringify({
            query,
            variables: { id: productGid },
          }),
        });

        const data = await response.json();
        const mediaNodes = data?.data?.node?.media?.nodes || [];
        const video = mediaNodes.find(media => media.mediaContentType === 'VIDEO') || null;
        const images = mediaNodes
          .filter(media => media.mediaContentType === 'IMAGE')
          .map(media => ({
            uri: media.image.url,
            height: media.image.height,
            width: media.image.width
          }));

        return { video, images };
      } catch (error) {
        console.error("Error fetching product media:", error);
        return { video: null, images: [] };
      }
    }

    async render() {
      const { img, title, index } = this._product || {};      
      // Get media data from GraphQL
      const { video, images } = await this.getProductMediaData();
      
      let html = '';
      
      if (video && video.sources && video.sources.length > 0) {
        // Use video as primary media
        const videoSource = video.sources[0];
        const width = videoSource.width ? 252.66 : undefined;
        const height = videoSource.width && videoSource.height 
          ? ((videoSource.height / videoSource.width) * 252.66).toFixed(2) 
          : undefined;
        
        html += `
          <video
            ${width ? `width="${width}"` : ''}
            ${height ? `height="${height}"` : ''}
            muted
            loop
            autoplay
            playsinline
            class="rcc-search__image rcc-search__image--primary"
            ${index > 8 ? '' : 'fetchpriority="high"'}
            preload="metadata"
          >
            <source src="${videoSource.url}" type="${videoSource.mimeType}">
          </video>
        `;
      } else {
        // Use GraphQL images if available, otherwise fall back to basic image
        const primaryImg = images[0] || img;
        const hoverImg = images[1]; // Second image for hover effect
        
        if (primaryImg) {
          const width = primaryImg.width ? 252.66 : undefined;
          const height = primaryImg.width && primaryImg.height 
            ? ((primaryImg.height / primaryImg.width) * 252.66).toFixed(2) 
            : undefined;
          
          html += `
              <img
                ${width ? `width="${width}"` : ''}
                ${height ? `height="${height}"` : ''}
                src="${this.imgURL(primaryImg.uri, '480x')}"
                alt="${title ? `${title} Product Image` : 'Product Image'}"
                class="rcc-search__image rcc-search__image--primary"
                ${index > 8 ? 'loading="lazy"' : 'fetchpriority="high"'}
              />
          `;
          
          // Add hover image for non-touch devices
          if (hoverImg && this.isNonTouchDevice) {
            const hoverWidth = hoverImg.width ? 252.66 : undefined;
            const hoverHeight = hoverImg.width && hoverImg.height 
              ? ((hoverImg.height / hoverImg.width) * 252.66).toFixed(2) 
              : undefined;
            
            html += `
              <img
                ${hoverWidth ? `width="${hoverWidth}"` : ''}
                ${hoverHeight ? `height="${hoverHeight}"` : ''}
                src="${this.imgURL(hoverImg.uri, '480x')}"
                alt="${title ? `${title} Product Image` : 'Product Image'}"
                class="rcc-search__image rcc-search__image--hover"
                loading="lazy"
              />
            `;
          }
        }
      }
      
      this.innerHTML = html;
    }
  }

  customElements.define("rcc-plp-media", ProductImage);
})();
